import React, { useEffect, useMemo, useState } from 'react';
import { Alert, alpha, Box, Button, IconButton, Stack, Theme, Typography } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import { ElementClarifyingQuestion, MeasureDateDefaultItem } from 'api/element-issues';
import useBundleTranslation from 'i18n';
import {
    answeredSelections,
    buildSubmitAnswer,
    initialSelections,
    isComplete,
    MeasureDateSelections,
    pageCount,
    pageSlice,
    selectedCount,
    validSelection,
} from './measure-date-defaults';
import ReactSelect from 'components/common/react-select/ReactSelect';

interface Props {
    question: ElementClarifyingQuestion;
    isSubmitting: boolean;
    /** Posts the structured answer JSON through the ordinary answer path; rejects on failure. */
    onSubmit: (answer: string) => Promise<void>;
    onSkip: () => void;
}

/**
 * Structured control for the grouped `measure_date_defaults` question: one row
 * per measure with its candidate Dates, a single submit once every open row has
 * a selection. Rows the server already recorded (partial answers, or the whole
 * question once answered) render locked: the backend rejects changing them.
 */
export default function MeasureDateDefaultsControl({ question, isSubmitting, onSubmit, onSkip }: Props) {
    const { t } = useBundleTranslation('app/extreport/concierge');

    const items = useMemo(() => question.items ?? [], [question.items]);
    const answered = useMemo(() => answeredSelections(question), [question]);

    const [selections, setSelections] = useState<MeasureDateSelections>(() => initialSelections(items, answered));
    const [page, setPage] = useState(0);
    const [stale, setStale] = useState(false);

    // Reset per question; keep in-progress selections when only server state (answered rows) moved.
    useEffect(() => {
        setSelections(initialSelections(items, answered));
        setPage(0);
        setStale(false);
    }, [question.questionId]);

    const empty = 0 === items.length;
    const totalPages = pageCount(items.length);
    const visibleItems = pageSlice(items, Math.min(page, totalPages - 1));
    const complete = !empty && isComplete(items, answered, selections);
    const selected = selectedCount(items, answered, selections);
    const allLocked = !empty && items.every((item) => !!answered[item.itemKey]);

    const handleSelect = (item: MeasureDateDefaultItem, value: string) => {
        if (answered[item.itemKey] || isSubmitting) return;
        setSelections((prev) => {
            const next = { ...prev };
            if (value) next[item.itemKey] = value;
            else delete next[item.itemKey];
            return next;
        });
    };

    const handleSubmit = async () => {
        if (!complete || allLocked || isSubmitting) return;
        setStale(false);
        try {
            await onSubmit(buildSubmitAnswer(items, answered, selections));
        } catch {
            // The optimistic patch is rolled back upstream; the recorded set no
            // longer matches what the user saw (superseded candidates or a
            // concurrent answer) — surface the reload message.
            setStale(true);
        }
    };

    return (
        <Stack>
            {empty && (
                <Alert severity="info" variant={'standard'} sx={{ my: 1 }}>
                    {t('clarifications_defaults_empty')}
                </Alert>
            )}
            {visibleItems.length > 0 && (
                <Stack sx={{ mt: 1.5, mb: 1 }} spacing={1}>
                    {visibleItems.map((item) => {
                        const locked = !!answered[item.itemKey];
                        // A drifted in-progress selection renders blank (and no longer counts);
                        // a locked value always renders, via a fallback option if it drifted.
                        const value = locked ? answered[item.itemKey] : (validSelection(item, selections) ?? '');
                        const lockedValueMissing = locked && !item.options.some((option) => option.value === value);
                        return (
                            <Stack key={item.itemKey} spacing={0.5}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    {locked && <IconMi icon="check" sx={{ fontSize: '16px', color: 'success.main' }} />}
                                    <Box sx={{ color: locked ? 'success.main' : undefined }}>{item.measureName}</Box>
                                </Stack>
                                {item.askReasonText && (
                                    <Typography
                                        sx={{ fontSize: '12px', color: 'text.secondary' }}
                                        data-test={`clarifications_defaults_reason_${item.itemKey}`}
                                    >
                                        {item.askReasonText}
                                    </Typography>
                                )}
                                <Box>
                                    <ReactSelect
                                        value={value}
                                        disabled={locked || isSubmitting}
                                        update={(value) => handleSelect(item, value)}
                                        aria-label={item.question}
                                        data-test={`clarifications_defaults_select_${item.itemKey}`}
                                        data={[
                                            ...(lockedValueMissing
                                                ? [
                                                      {
                                                          label: value,
                                                          value: value,
                                                      },
                                                  ]
                                                : []),
                                            ...(item.options ?? []).map((option) => {
                                                return {
                                                    label: option.label,
                                                    value: option.value,
                                                };
                                            }),
                                        ]}
                                        placeholder={t('clarifications_defaults_placeholder')}
                                        customPortalTarget={null}
                                    />
                                </Box>
                            </Stack>
                        );
                    })}
                </Stack>
            )}

            {/* In-card pagination for large groups */}
            {totalPages > 1 && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={0.5}
                    sx={{
                        my: 1,
                    }}
                >
                    <IconButton
                        size="small"
                        disabled={0 === page || isSubmitting}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        sx={{ p: 0.5, color: 'text.secondary' }}
                    >
                        <IconMi icon="chevron-left" sx={{ fontSize: '16px' }} />
                    </IconButton>
                    <Typography sx={{ color: (theme) => alpha(theme.palette.text.primary, 0.8), whiteSpace: 'nowrap' }}>
                        {t('clarifications_pager', { current: Math.min(page, totalPages - 1) + 1, total: totalPages })}
                    </Typography>
                    <IconButton
                        size="small"
                        disabled={page >= totalPages - 1 || isSubmitting}
                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                        sx={{ p: 0.5, color: 'text.secondary' }}
                    >
                        <IconMi icon="chevron-right" sx={{ fontSize: '16px' }} />
                    </IconButton>
                </Stack>
            )}
            {stale && (
                <Alert severity="error" variant={'standard'} sx={{ my: 1 }}>
                    {t('clarifications_defaults_stale')}
                </Alert>
            )}
            {/* Footer: progress, skip, submit */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={1}
                sx={{
                    mx: '-12px',
                    px: 1.5,
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: (theme) => theme.palette.divider,
                }}
            >
                {!empty && (
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', flexGrow: 1 }}>
                        {t('clarifications_defaults_selected_count', { selected, total: items.length })}
                    </Typography>
                )}
                {!allLocked && (
                    <>
                        <Typography
                            onClick={() => !isSubmitting && onSkip()}
                            data-test="clarifications_defaults_skip"
                            sx={{
                                color: 'text.secondary',
                                cursor: 'pointer',
                                userSelect: 'none',
                                flexShrink: 0,
                                '&:hover': { color: 'primary.main' },
                            }}
                        >
                            {t('clarifications_skip')}
                        </Typography>
                        {!empty && (
                            <Button
                                size="small"
                                variant="contained"
                                disabled={!complete || isSubmitting}
                                onClick={handleSubmit}
                                data-test="clarifications_defaults_submit"
                            >
                                {t('clarifications_defaults_submit')}
                            </Button>
                        )}
                    </>
                )}
            </Stack>
        </Stack>
    );
}
